/**
 * FORMATTING HELPERS
 * ------------------
 * Small functions that turn raw data into something readable on screen.
 * Kept in one file so dates and money look the same everywhere on the site.
 *
 * WHICH LANGUAGE THE DATES COME OUT IN
 *
 * These are plain functions, called from about forty places, and they are not
 * hooks - so they cannot read the language out of React context themselves.
 * Rather than add a `locale` argument to all forty call sites, the provider
 * pushes the current locale in here whenever it changes, with
 * `setFormatLocale()` below.
 *
 * That is one piece of module-level state, which is worth being uneasy about,
 * so two things keep it honest. It is set **synchronously in the body of
 * I18nProvider**, not in an effect - a child renders after its parent, so the
 * locale is always right before anything formats a date, and there is no first
 * paint in the wrong language. And there is exactly one setter, in one file,
 * called from one place.
 *
 * 'en-GB' rather than 'en' for the English side: plain 'en' means American and
 * puts the month first. See section 10b of HANDOFF.md.
 */

// Only for the fallback in phrase() below - see the note there. en.js
// imports nothing, so this cannot make a loop.
import en from '../i18n/en';

let locale = 'en-GB';

/**
 * The translator, pushed in the same way and for the same reason as the
 * locale above.
 *
 * A date can be handed to toLocaleDateString and comes out in the right
 * language on its own. "3 days ago" cannot: those words are ours, and until
 * this existed they were written out in English here, so an Italian reader
 * got Italian everywhere except every relative time and distance on the
 * site - which is most screens.
 *
 * Set in the body of I18nProvider, synchronously, at the same moment as the
 * locale. Everything that formats anything renders below that provider, so
 * it is always in place first.
 */
let translate = null;

/** Called by I18nProvider. Nothing else should call this. */
export function setFormatLocale(next, t) {
  locale = next || 'en-GB';
  if (t) translate = t;
}

/**
 * Looks a phrase up, falling back to English if the provider has somehow
 * not run yet. That should be impossible for anything rendered inside it,
 * and the fallback is here so a mistake shows English rather than crashing
 * a page over a date.
 *
 * Exported since 24 August 2026 so utils/schedule.js can use it. That file
 * writes out when a chore happens, and two of the things it now has to say -
 * "flexible" and the word joining a date to a time - are words rather than
 * numbers, so they cannot be built out of Intl alone.
 *
 * THE SUBSTITUTION BELOW WAS BROKEN and was fixed at the same time. It read
 * `/{(w+)}/g`, which matches a brace around a run of the letter w - so
 * `{count}` and `{name}` were never replaced and would have been printed
 * literally. It only ever ran before the language provider had started, which
 * is why nobody saw it, and it would have started showing the moment anything
 * on this path took a value.
 */
export function phrase(key, values) {
  if (translate) return translate(key, values);

  const found = key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), en);
  const text =
    found && typeof found === 'object'
      ? (Number(values?.count) === 1 ? found.one : found.other)
      : found;
  if (typeof text !== 'string') return key;
  return text.replace(/\{(\w+)\}/g, (whole, name) =>
    (values && name in values ? String(values[name]) : whole));
}

export function currentLocale() {
  return locale;
}

/** 30 -> "€30"  |  30.5 -> "€30.50" */
export function money(amount) {
  const value = Number(amount) || 0;
  return Number.isInteger(value) ? `€${value}` : `€${value.toFixed(2)}`;
}

/** "2025-08-14T10:00:00Z" -> "Thu 14 Aug, 10:00" */
export function dateTime(value) {
  if (!value) return '';
  return new Date(value).toLocaleString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "2025-08-14T10:00:00Z" -> "14 Aug 2025" */
export function dateOnly(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * A date of birth: "17 March 2009" / "17 marzo 2009".
 *
 * Read in UTC, always. The server stores a birthday as midnight UTC on that
 * day (backend/utils/age.js), and read in local time that is the day BEFORE
 * anywhere west of London - somebody opening the site on holiday in New York
 * would see their own birthday a day early.
 */
export function birthDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** The twelve month names in the site's language, January first. */
export function monthNames() {
  return Array.from({ length: 12 }, (_, i) =>
    new Date(Date.UTC(2000, i, 1)).toLocaleDateString(locale, { month: 'long', timeZone: 'UTC' }));
}

/**
 * How old somebody born on "2009-03-17" is today, or null.
 *
 * Only for saying it back to them while they fill in a form ("that makes you
 * 17"), which catches a slipped year before it is sent. The SERVER works out
 * the age that counts, on Italian dates - this is a courtesy, not a rule.
 */
export function ageFromBirthDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  const today = new Date();
  let age = today.getFullYear() - year;
  const m = today.getMonth() + 1;
  if (m < month || (m === month && today.getDate() < day)) age -= 1;
  return age;
}

/** How long ago something happened: "3 days ago", "just now". */
export function timeAgo(value) {
  if (!value) return '';
  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);

  if (seconds < 60) return phrase('time.justNow');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return phrase('time.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return phrase('time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return phrase('time.daysAgo', { count: days });
  const months = Math.floor(days / 30);
  if (months < 12) return phrase('time.monthsAgo', { count: months });
  return phrase('time.yearsAgo', { count: Math.floor(months / 12) });
}

/**
 * Turns hours-from-now into "in 3 days", "in 5 hours".
 *
 * Always rounds DOWN. 23.7 hours must read "in 23 hours", not "in 24 hours" -
 * otherwise the countdown contradicts the 24-hour cancellation warning sitting
 * right next to it.
 */
export function countdown(hours) {
  if (hours === null || hours === undefined) return '';
  if (hours < 0) return phrase('time.inThePast');
  if (hours < 1) return phrase('time.inMinutes', { count: Math.max(1, Math.floor(hours * 60)) });
  if (hours < 24) return phrase('time.inHours', { count: Math.floor(hours) });
  return phrase('time.inDays', { count: Math.floor(hours / 24) });
}

/** "Aisha", "Khan" -> "AK" (used when there is no profile picture) */
export function initials(firstName = '', lastName = '') {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
}

/**
 * Has this person closed their account?
 *
 * When somebody deletes their account we strip their personal details but
 * keep the record, so that the reviews they wrote and the chores they did
 * stay attached to everybody else's history. Those records then need to show
 * SOMETHING in place of a name.
 */
export function isDeleted(person) {
  return Boolean(person && person.deletedAt);
}

/**
 * How to show a person's name publicly.
 *
 *   normal    -> "Aisha K."
 *   deleted   -> "Deleted user"
 *
 * Always use this rather than building the name inline, otherwise a closed
 * account renders as nonsense like "Deleted u.".
 */
export function displayName(person) {
  if (!person) return phrase('misc.someone');
  if (isDeleted(person)) return phrase('misc.deletedUser');

  const first = person.firstName || '';
  const initial = person.lastName ? `${person.lastName.charAt(0)}.` : '';
  return `${first} ${initial}`.trim() || phrase('misc.someone');
}

/** 3.2 -> "3.2 km away", null -> "" */
export function distance(km) {
  if (km === null || km === undefined) return '';
  if (km < 1) return phrase('misc.metresAway', { count: Math.round(km * 1000) });
  return phrase('misc.kmAway', { count: km });
}

/**
 * How far away something is, from the distance fields the server sends
 * (backend/utils/routes.js): "6.4 km by road · 5.1 km on foot" - or, when the
 * route planner could not answer, "6.4 km in a straight line", so nobody
 * mistakes one for the other.
 *
 * Takes the whole thing - a chore, an applicant, an offer, a profile - rather
 * than a number, because which words are right depends on `distanceKind`.
 * Something without one (an older server) reads as it always did.
 *
 * Numbers follow the page's language: 6.4 in English, 6,4 in Italian.
 */
export function travel(item) {
  if (!item || item.distanceKm === null || item.distanceKm === undefined) return '';

  const part = (km, how) => {
    if (km < 1) return phrase(`misc.m${how}`, { count: Math.round(km * 1000) });
    const count = km.toLocaleString(locale, { maximumFractionDigits: 1 });
    return phrase(`misc.km${how}`, { count });
  };

  if (item.distanceKind === 'road') {
    const road = part(item.roadKm ?? item.distanceKm, 'ByRoad');
    const hasFoot = item.footKm !== null && item.footKm !== undefined;
    return hasFoot ? `${road} · ${part(item.footKm, 'OnFoot')}` : road;
  }
  if (item.distanceKind === 'straight') return part(item.distanceKm, 'Straight');
  return distance(item.distanceKm);
}

/**
 * Checks a link is somewhere inside this site before we navigate to it.
 *
 * Notification links come from the database, so they should always be
 * internal paths like /chores/123. Verifying that here means a bad value
 * could never bounce somebody to another website - a trick used to make
 * phishing pages look like they came from us.
 *
 * Rejects anything that is not a plain path: full URLs (https://evil.com),
 * protocol-relative (//evil.com), and backslash tricks (/\evil.com) that
 * some browsers quietly treat as a domain.
 */
export function safeInternalPath(link) {
  if (typeof link !== 'string' || !link) return null;
  if (!link.startsWith('/')) return null;
  if (link.startsWith('//') || link.startsWith('/\\')) return null;
  if (/^\/+[\\/]/.test(link)) return null;
  return link;
}

/** Full URL for an uploaded profile picture, or null. */
export function photoUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return path; // Vite proxies /uploads straight through to the backend
}

/**
 * The status labels used to live here as two tables. They moved into the
 * dictionaries when the site went bilingual - `status.*` and
 * `applicationStatus.*` in i18n/en.js - because a label that has to exist in
 * two languages belongs where the other two thousand of them are, and a table
 * here would have been a third place to remember.
 *
 * The KEYS are still the values the database stores. Only the label is
 * translated. See the warning at the top of i18n/en.js.
 */
